// 화면 품질·FPS 채널(설계서 §9.5) — 에이전트가 만든 "ctrl" DataChannel 을 감싼다.
//
// 규약은 `protocol.ts`(에이전트 `ctrl.rs` 와 같은 표). 이 파일은 채널 수명·콜백만 맡는다.
// 🔴 이 채널은 **보기 전용 세션에도 온다** — 화질은 원격 기기를 조작하는 것이 아니라 보는 쪽의 편의라서다.

import {
  CTRL_MSG,
  CTRL_ERR_MESSAGE,
  encodeRecording,
  encodeSet,
  parseApplied,
  parseCaps,
  parseRecordingAck,
  type QualityApplied,
  type QualityCaps,
  type QualitySpec,
} from './protocol';

export class QualityChannel {
  private readonly dc: RTCDataChannel;
  private caps: QualityCaps | null = null;
  /** 녹화 고지 확인(`rok`) 대기 중인 약속 — 보낸 값별로 하나. §9.7.1 */
  private recAck: { on: boolean; resolve: (ok: boolean) => void; timer: number } | null = null;

  constructor(
    dc: RTCDataChannel,
    private readonly onReady: (caps: QualityCaps) => void,
    private readonly onApplied: (applied: QualityApplied) => void,
    private readonly onError: (message: string) => void,
  ) {
    this.dc = dc;
    dc.onmessage = (ev) => this.handle(ev.data);
  }

  private handle(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }
    let v: Record<string, unknown>;
    try {
      v = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return; // 비-JSON 무시
    }
    if (v.t === CTRL_MSG.CAP) {
      const caps = parseCaps(v);
      if (caps) {
        this.caps = caps;
        this.onReady(caps);
      }
      return;
    }
    if (v.t === CTRL_MSG.OK) {
      const applied = parseApplied(v);
      if (applied) {
        this.onApplied(applied);
      }
      return;
    }
    if (v.t === CTRL_MSG.ERR) {
      const code = typeof v.code === 'string' ? v.code : '';
      this.onError(CTRL_ERR_MESSAGE[code] ?? '화질을 바꾸지 못했습니다.');
      return;
    }
    // ── 세션 녹화 고지 확인(§9.7.1) ──────────────────────────────────────
    if (v.t === CTRL_MSG.REC_OK) {
      const ack = parseRecordingAck(v);
      // 보낸 값과 같은 확인만 받아들인다(늦게 온 반대쪽 확인이 다음 요청을 풀어 주면 안 된다).
      if (ack && this.recAck && this.recAck.on === ack.on) {
        this.settleRecAck(true);
      }
      return;
    }
    if (v.t === CTRL_MSG.REC_ERR) {
      this.settleRecAck(false);
    }
  }

  /** 대기 중인 녹화 고지 확인을 끝낸다(성공/실패 어느 쪽이든 타이머를 함께 정리). */
  private settleRecAck(ok: boolean): void {
    const pending = this.recAck;
    if (!pending) {
      return;
    }
    this.recAck = null;
    clearTimeout(pending.timer);
    pending.resolve(ok);
  }

  /** 현재 능력(없으면 아직 qcap 전). */
  get capabilities(): QualityCaps | null {
    return this.caps;
  }

  get ready(): boolean {
    return this.caps !== null && this.dc.readyState === 'open';
  }

  /** 품질·FPS 변경 요청. 빠진 값은 에이전트가 현재 값을 유지한다. */
  set(spec: Partial<QualitySpec>): void {
    if (this.dc.readyState !== 'open') {
      return;
    }
    this.dc.send(encodeSet(spec));
  }

  /** 이 에이전트가 녹화 고지를 화면에 표시할 수 있는가(§9.7.2). 모르면 false. */
  get canNotifyRecording(): boolean {
    return this.caps?.recording === true;
  }

  /**
   * 녹화 고지를 보내고 **표시됐다는 확인을 기다린다**(§9.7.1).
   *
   * 🔴 호출부는 이 약속이 `true` 로 풀린 **뒤에** 녹화를 시작해야 한다. 이 채널의 메시지 처리는
   * 화질 반영 확인 때문에 최대 2초 밀릴 수 있어서, 먼저 시작하면 고지 없이 녹화되는 구간이 생긴다.
   *
   * 확인이 오지 않으면 `false` — 호출부는 녹화하지 않는다("고지할 수 없으면 녹화도 없다").
   */
  notifyRecording(on: boolean, timeoutMs = 4000): Promise<boolean> {
    if (this.dc.readyState !== 'open' || !this.canNotifyRecording) {
      return Promise.resolve(false);
    }
    // 앞선 대기가 남아 있으면 실패로 정리한다(요청이 겹치면 뒤엣것만 유효하다).
    this.settleRecAck(false);
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => this.settleRecAck(false), timeoutMs) as unknown as number;
      this.recAck = { on, resolve, timer };
      try {
        this.dc.send(encodeRecording(on));
      } catch {
        this.settleRecAck(false);
      }
    });
  }
}
